import { LandingTrust } from "../components/LandingTrust";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  ShieldCheck, BellRing, FileCheck2, Users, BarChart3, Boxes, PenLine,
  MessageCircle, CheckCircle2, ArrowLeft, Smartphone, Loader2, X, Star, Timer, Gift, Sparkles,
  Mail, Phone, Linkedin, Facebook, Twitter, Youtube,
} from "lucide-react";
import { SUPPORT_WHATSAPP, TRIAL_DAYS } from "../lib/constants";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { EmployeeLoginDialog } from "../components/EmployeeLoginDialog";
import { Building2, UserCheck, Download, Fingerprint } from "lucide-react";
import { pickText, ICON_MAP, contactPreset, DEFAULT_CONTACT_HEADING, DEFAULT_CONTACT_SUB, DEFAULT_CARDS, DEFAULT_FOOTER_TAGLINE, DEFAULT_SOCIAL, DEFAULT_FOOTER_COLS } from "../lib/landingConfig";
import { loginWithPasskeyDiscoverable, passkeyPlatformAvailable } from "../lib/passkey";

const login = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const PhoneLoginDialog = ({ open, onOpenChange }) => {
  const { setUser, refreshSubscription } = useAuth();
  const { t, dir } = useLang();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [mode, setMode] = useState("login");      // "login" | "forgot"
  const [resetStep, setResetStep] = useState(1);  // 1 = enter phone, 2 = enter code + new pass
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");

  useEffect(() => { passkeyPlatformAvailable().then(setBioSupported); }, []);

  const doPasskeyLogin = async () => {
    setBioBusy(true);
    try {
      const u = await loginWithPasskeyDiscoverable("manager");
      setUser(u.user || u);
      await refreshSubscription();
      toast.success("تم الدخول بنجاح");
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "تم إلغاء العملية"
        : (e?.response?.data?.detail || "لا توجد بصمة مُفعّلة — ادخل مرة ثم فعّل البصمة من الإعدادات"));
    } finally { setBioBusy(false); }
  };

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpMsg, setOtpMsg] = useState("");
  const sendOtp = async (channel = "email") => {
    if (phone.replace(/\D/g, "").length < 9) { toast.warning("اكتب رقم جوالك المسجّل"); return; }
    setBusy(true);
    try {
      const r = await api.otpStart(phone.trim(), channel);
      setOtpSent(true); setOtp("");
      if (r.cooldown) toast.info("انتظر 30 ثانية قبل طلب رمز جديد");
      else if (channel === "email") r.sent ? toast.success(`أُرسل الرمز إلى ${r.email_masked}`) : toast.error(r.no_email ? "لا يوجد بريد مسجّل لهذا الحساب" : "تعذّر الإرسال على الإيميل");
      setOtpMsg(channel === "email" ? (r.sent ? `أرسلنا الرمز إلى بريدك ${r.email_masked}` : "لم نتمكن من الإرسال على الإيميل — جرّب «أرسل على واتساب»") : `إذا كان الرقم ${r.phone_masked} مسجّلاً سيصلك رمز من 6 أرقام على واتساب`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إرسال الرمز"); } finally { setBusy(false); }
  };
  const [useOtp, setUseOtp] = useState(false);
  const submit = async () => {
    if (useOtp && !otpSent) { sendOtp(); return; }
    if (useOtp && otp.length !== 6) { toast.warning("اكتب الرمز المكوّن من 6 أرقام"); return; }
    if (!useOtp && (!phone.trim() || !password)) { toast.warning(t("landing.phoneLogin.warnCreds")); return; }
    setBusy(true);
    try {
      const u = useOtp ? await api.otpVerify(phone.trim(), otp) : await api.phoneLogin(phone.trim(), password);
      setUser(u);
      await refreshSubscription();
      toast.success(`${t("landing.phoneLogin.welcome")} ${u.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("landing.phoneLogin.loginFail"));
    } finally { setBusy(false); }
  };

  const sendResetCode = async () => {
    if (!phone.trim()) { toast.warning(t("landing.phoneLogin.warnForgot")); return; }
    setBusy(true);
    try {
      const res = await api.ownerResetRequest(phone.trim());
      if (res.sent) {
        setMaskedPhone("");
        setResetStep(2);
        toast.success("إذا كان الرقم مسجّلاً وله بريد إلكتروني، سيصلك رمز من 6 أرقام على البريد");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر إرسال الرمز");
    } finally { setBusy(false); }
  };

  const confirmReset = async () => {
    if (!code.trim() || code.trim().length < 4) { toast.warning("أدخل الرمز المُرسَل"); return; }
    if (!/^\d{6}$/.test(newPass || "")) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); return; }
    setBusy(true);
    try {
      await api.ownerResetConfirm(phone.trim(), code.trim(), newPass);
      const u = await api.me();
      setUser(u);
      await refreshSubscription();
      toast.success("تم تغيير كلمة المرور والدخول بنجاح");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "الرمز غير صحيح");
    } finally { setBusy(false); }
  };

  const backToLogin = () => { setMode("login"); setResetStep(1); setCode(""); setNewPass(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="phone-login-dialog">
        <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
          <DialogTitle className="font-heading text-xl">{t("landing.phoneLogin.title")}</DialogTitle>
          <DialogDescription className={dir === "rtl" ? "text-right" : "text-left"}>{t("landing.phoneLogin.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {mode === "login" ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="login-phone">{t("landing.phoneLogin.phoneLabel")}</Label>
                <Input id="login-phone" data-testid="login-phone-input" dir="ltr" inputMode="tel" placeholder="05xxxxxxxx"
                  value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                <button type="button" data-testid="login-mode-password" onClick={() => setUseOtp(false)}
                  className={`rounded-lg py-1.5 text-xs font-semibold transition ${!useOtp ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>كلمة المرور</button>
                <button type="button" data-testid="login-mode-otp" onClick={() => setUseOtp(true)}
                  className={`rounded-lg py-1.5 text-xs font-semibold transition ${useOtp ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>رمز على الإيميل</button>
              </div>
              {!useOtp && (
                <div className="space-y-1.5">
                  <Label htmlFor="login-pass">{t("landing.phoneLogin.passLabel")}</Label>
                  <Input id="login-pass" data-testid="login-password-input" type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
                </div>
              )}
              {useOtp && otpSent && (
                <div className="space-y-1.5" data-testid="otp-step">
                  <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="otp-msg">{otpMsg}</p>
                  <Label htmlFor="login-otp">رمز الدخول</Label>
                  <Input id="login-otp" data-testid="login-otp-input" dir="ltr" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="••••••"
                    className="text-center font-heading text-xl tracking-[0.5em]" autoFocus
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && submit()} />
                </div>
              )}
              <Button data-testid="phone-login-submit" disabled={busy || (useOtp && otpSent && otp.length !== 6)} onClick={submit}
                className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} {!useOtp ? t("landing.phoneLogin.submit") : otpSent ? "دخول" : "أرسل رمز الدخول على الإيميل"}
              </Button>
              {!useOtp && (
                <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setResetStep(1); }}
                  className="w-full text-center text-xs text-slate-400 hover:text-emerald-600 transition">{t("landing.phoneLogin.forgot")}</button>
              )}
              {useOtp && otpSent && (
                <div className="flex items-center justify-between text-xs">
                  <button type="button" data-testid="otp-resend" disabled={busy} onClick={() => sendOtp("email")} className="text-slate-500 hover:text-emerald-600">إعادة الإرسال</button>
                  <button type="button" data-testid="otp-whatsapp" disabled={busy} onClick={() => sendOtp("whatsapp")} className="text-slate-500 hover:text-blue-600">أرسل على واتساب</button>
                  <button type="button" data-testid="otp-change-phone" onClick={() => { setOtpSent(false); setOtp(""); }} className="text-slate-500 hover:text-slate-800">تغيير الرقم</button>
                </div>
              )}
              <div className="relative py-1 text-center">
                <span className="text-xs text-slate-400 bg-white px-2 relative z-10">{t("landing.phoneLogin.or")}</span>
                <div className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
              </div>
              <Button variant="outline" data-testid="google-login-button" onClick={login} className="w-full gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> {t("landing.phoneLogin.google")}
              </Button>
              {bioSupported && (
                <Button variant="ghost" data-testid="mgr-passkey-login" disabled={bioBusy} onClick={doPasskeyLogin}
                  className="w-full gap-2 text-emerald-700 hover:bg-emerald-50">
                  {bioBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} الدخول بالبصمة
                </Button>
              )}
            </>
          ) : (
            <div className="space-y-3" data-testid="forgot-flow">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 leading-relaxed text-right">
                سنرسل رمزاً من 6 أرقام إلى بريدك الإلكتروني المسجّل لإعادة تعيين كلمة المرور. إذا لم يكن لحسابك بريد مسجّل تواصل مع الدعم.
              </div>
              {resetStep === 1 ? (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-phone">رقم الجوال</Label>
                    <Input id="reset-phone" data-testid="reset-phone-input" dir="ltr" inputMode="tel" placeholder="05xxxxxxxx"
                      value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendResetCode()} />
                  </div>
                  <Button data-testid="reset-send-code" disabled={busy} onClick={sendResetCode}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} إرسال الرمز على الإيميل
                  </Button>
                </>
              ) : (
                <>
                  {maskedPhone && <p className="text-xs text-slate-500 text-center">تم إرسال الرمز إلى {maskedPhone}</p>}
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-code">رمز التحقق (6 أرقام)</Label>
                    <Input id="reset-code" data-testid="reset-code-input" dir="ltr" inputMode="numeric" maxLength={6} placeholder="______"
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-newpass">كلمة المرور الجديدة</Label>
                    <Input id="reset-newpass" data-testid="reset-newpass-input" pin
                      value={newPass} onChange={(e) => setNewPass(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmReset()} />
                  </div>
                  <Button data-testid="reset-confirm" disabled={busy} onClick={confirmReset}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} تعيين كلمة المرور والدخول
                  </Button>
                  <button type="button" onClick={sendResetCode} disabled={busy}
                    className="w-full text-center text-xs text-slate-400 hover:text-emerald-600 transition">
                    إعادة إرسال الرمز
                  </button>
                </>
              )}
              <button type="button" data-testid="reset-back" onClick={backToLogin}
                className="w-full text-center text-xs text-slate-400 hover:text-emerald-600 transition">
                ← العودة لتسجيل الدخول
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const waLink = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent("مرحباً، أرغب بالاشتراك في تطبيق WorkZone")}`;

const FEATURE_ICONS = [BellRing, FileCheck2, Boxes, BarChart3, Users, PenLine];

const Feature = ({ icon: Icon, title, desc, i, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.4, delay: i * 0.05 }}
    className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:border-emerald-200 transition"
    data-testid={`landing-feature-${i}`}>
    <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4"
      style={{ backgroundColor: `${accent}1a`, color: accent }}>
      <Icon className="h-6 w-6" />
    </div>
    <h3 className="font-heading font-bold text-slate-800 text-lg">{title}</h3>
    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{desc}</p>
  </motion.div>
);

const PLANS = [
  { id: "starter", price: "99", original: "149" },
  { id: "business", price: "199", original: "299", popular: true },
  { id: "professional", price: "299", original: "449" },
  { id: "enterprise", price: null },
];

const pad2 = (n) => String(n).padStart(2, "0");
const OfferCountdown = () => {
  const { t } = useLang();
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      setLeft(Math.max(0, Math.floor((end - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const hh = Math.floor(left / 3600);
  const mm = Math.floor((left % 3600) / 60);
  const ss = left % 60;
  return (
    <div data-testid="offer-countdown"
      className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 shadow-sm">
      <Timer className="h-4 w-4 animate-pulse" /> {t("landing.pricing.offerEndsIn")}
      <span className="font-mono-nums tabular-nums text-base text-amber-900" dir="ltr">{pad2(hh)}:{pad2(mm)}:{pad2(ss)}</span>
    </div>
  );
};

const BillingToggle = ({ billing, setBilling }) => {
  const { t } = useLang();
  return (
    <div data-testid="billing-toggle" className="inline-flex items-center rounded-full bg-slate-100 p-1 text-sm font-semibold">
      <button type="button" data-testid="billing-monthly" onClick={() => setBilling("monthly")}
        className={`rounded-full px-4 py-1.5 transition ${billing === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
        {t("landing.pricing.billingMonthly")}
      </button>
      <button type="button" data-testid="billing-annual" onClick={() => setBilling("annual")}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition ${billing === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
        {t("landing.pricing.billingAnnual")}
        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-extrabold text-emerald-700">{t("landing.pricing.saveTwoMonths")}</span>
      </button>
    </div>
  );
};

const PlanCard = ({ plan, i, billing = "monthly" }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const subscribeNow = async () => {
    if (!user) {
      localStorage.setItem("wz_pending_plan", plan.id);
      login();
      return;
    }
    setPaying(true);
    try {
      const data = await api.paymobSubscribe(plan.id);
      if (data?.checkout_url) window.location.assign(data.checkout_url);
      else toast.error("تعذّر بدء عملية الدفع، حاول مجدداً");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء عملية الدفع");
      setPaying(false);
    }
  };
  const isEnterprise = plan.price === null || plan.price === "" || plan.price === undefined;
  const annual = billing === "annual";
  const shownPrice = annual ? Number(plan.price) * 10 : Number(plan.price);
  const shownOriginal = plan.original ? (annual ? Number(plan.original) * 12 : Number(plan.original)) : null;
  const period = annual ? t("landing.pricing.perYear") : t("landing.pricing.perMonth");
  const discount = shownOriginal ? Math.round((1 - shownPrice / shownOriginal) * 100) : 0;
  const name = plan.name ?? t(`landing.plans.${plan.id}.name`);
  const tagline = plan.tagline ?? t(`landing.plans.${plan.id}.tagline`);
  const features = Array.isArray(plan.features) ? plan.features : (Array.isArray(t(`landing.plans.${plan.id}.features`)) ? t(`landing.plans.${plan.id}.features`) : []);
  const missing = Array.isArray(plan.missing) ? plan.missing : (Array.isArray(t(`landing.plans.${plan.id}.missing`)) ? t(`landing.plans.${plan.id}.missing`) : []);
  const enterpriseWa = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent("مرحباً، أرغب بعرض سعر لباقة Enterprise في WorkZone")}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.45, delay: i * 0.08 }}
      data-testid={`landing-plan-${plan.id}`}
      className={`relative flex flex-col rounded-3xl border bg-white p-6 transition ${
        plan.popular ? "border-emerald-500 ring-2 ring-emerald-500/30 shadow-2xl lg:-translate-y-3" : "border-slate-200 hover:border-emerald-200 hover:shadow-lg"
      }`}>
      {plan.popular && (
        <span className="absolute -top-3.5 right-6 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white shadow">
          <Star className="h-3 w-3 fill-white" /> {t("landing.pricing.mostPopular")}
        </span>
      )}
      {plan.id === "professional" && (
        <span data-testid="plan-badge-best-value" className="absolute -top-3.5 right-6 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow">
          <Sparkles className="h-3 w-3 fill-white" /> {t("landing.pricing.bestValue")}
        </span>
      )}
      <div>
        <h3 className="font-heading text-xl font-extrabold text-slate-900">{name}</h3>
        <p className="text-xs text-slate-400 mt-0.5">{tagline}</p>
      </div>
      {isEnterprise ? (
        <div className="mt-4 flex items-end gap-1.5">
          <span className="font-heading text-2xl font-extrabold text-slate-900">{t("landing.pricing.perRequest")}</span>
        </div>
      ) : (
        <div className="mt-4">
          {shownOriginal && (
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-slate-400 line-through decoration-rose-300 decoration-2 font-mono-nums" dir="ltr">{shownOriginal.toLocaleString("en-US")}</span>
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-extrabold text-rose-600">{t("landing.pricing.discount")} {discount}%</span>
            </div>
          )}
          <div className="mt-0.5 flex items-end gap-1.5">
            <span className="font-heading text-4xl font-extrabold text-slate-900 font-mono-nums">{shownPrice.toLocaleString("en-US")}</span>
            <span className="mb-1 text-sm text-slate-500">{period}</span>
          </div>
          {annual && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
              <Gift className="h-3 w-3" /> {t("landing.pricing.twoMonthsFreeAnnual")}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
          <BellRing className="h-3 w-3" /> {t("landing.pricing.freeTrial14")}
        </span>
        {!isEnterprise && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700 animate-pulse">
            <Timer className="h-3 w-3" /> {t("landing.pricing.limitedOffer")}
          </span>
        )}
      </div>

      {isEnterprise ? (
        <a href={enterpriseWa} target="_blank" rel="noreferrer" data-testid={`landing-plan-cta-${plan.id}`}
          className="mt-5 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white transition hover:bg-slate-800">
          <MessageCircle className="h-4 w-4" /> {t("landing.pricing.requestQuote")}
        </a>
      ) : (
        <div className="mt-5 space-y-2">
          <Button data-testid={`landing-plan-pay-${plan.id}`} onClick={subscribeNow} disabled={paying}
            className={`h-11 w-full font-semibold gap-2 ${plan.popular ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}`}>
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {paying ? "جارٍ التحويل للدفع…" : "اشترك الآن"}
          </Button>
          <button data-testid={`landing-plan-cta-${plan.id}`} onClick={login}
            className="w-full text-center text-xs font-medium text-emerald-700 hover:underline">
            {t("landing.pricing.startTrialBtn")}
          </button>
        </div>
      )}

      <ul className="mt-6 space-y-2.5 border-t border-slate-100 pt-5">
        {features.map((f, idx) => (
          <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-700">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" /> <span>{f}</span>
          </li>
        ))}
        {missing.map((f, idx) => (
          <li key={`m${idx}`} className="flex items-start gap-2.5 text-sm text-slate-400">
            <X className="h-4 w-4 shrink-0 text-slate-300 mt-0.5" /> <span className="line-through decoration-slate-300">{f}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const LoginChooser = ({ open, onOpenChange, onCompany, onEmployee }) => {
  const { t, dir } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="login-chooser-dialog">
        <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
          <DialogTitle className="font-heading text-xl">{t("landing.chooser.title")}</DialogTitle>
          <DialogDescription className={dir === "rtl" ? "text-right" : "text-left"}>{t("landing.chooser.desc")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="choose-company-login" onClick={onCompany}
            className="group rounded-2xl border border-slate-200 bg-white p-5 text-center hover:border-emerald-400 hover:shadow-md transition">
            <div className="mx-auto h-12 w-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:scale-105 transition">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="font-heading font-bold text-slate-900">{t("landing.chooser.company")}</div>
            <div className="text-xs text-slate-500 mt-1">{t("landing.chooser.companySub")}</div>
          </button>
          <button data-testid="choose-employee-login" onClick={onEmployee}
            className="group rounded-2xl border border-slate-200 bg-white p-5 text-center hover:border-emerald-400 hover:shadow-md transition">
            <div className="mx-auto h-12 w-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:scale-105 transition">
              <UserCheck className="h-6 w-6" />
            </div>
            <div className="font-heading font-bold text-slate-900">{t("landing.chooser.employee")}</div>
            <div className="text-xs text-slate-500 mt-1">{t("landing.chooser.employeeSub")}</div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Landing() {
  const { t, dir, lang } = useLang();
  const { platform } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [empLoginOpen, setEmpLoginOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [legal, setLegal] = useState(null); // "terms" | "privacy"
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/manifest-company.json");
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => {
      if (link && prev) link.setAttribute("href", prev);
      window.removeEventListener("beforeinstallprompt", onPrompt);
    };
  }, []);

  const doInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      try { await installPrompt.userChoice; } catch (_) {}
      setInstallPrompt(null);
      return;
    }
    toast.info('لتثبيت التطبيق: افتح قائمة المتصفح (⋮) ثم اختر "تثبيت التطبيق" أو "إضافة إلى الشاشة الرئيسية".');
  };

  const L = platform?.landing || {};
  const theme = L.theme || {};
  const brand = platform?.theme?.palette || null;
  const accent = brand?.[600] || theme.primary || "#059669";
  const heroFrom = theme.heroFrom || (brand ? brand[900] : "#064e3b");
  const heroTo = theme.heroTo || "#0f172a";
  const heroStyle = { backgroundImage: `linear-gradient(to left, ${heroFrom}, ${heroTo} 60%, ${heroTo})` };

  const heroBadge = pickText(L.hero?.badge, lang, t("landing.hero.badge", { days: TRIAL_DAYS }));
  const heroTitle = pickText(L.hero?.title, lang, t("landing.hero.title"));
  const heroSubtitle = pickText(L.hero?.subtitle, lang, t("landing.hero.subtitle"));
  const heroStart = pickText(L.hero?.startTrial, lang, t("landing.hero.startTrial"));
  const heroWa = pickText(L.hero?.whatsapp, lang, t("landing.hero.whatsapp"));

  const featHeading = pickText(L.features?.heading, lang, t("landing.features.heading"));
  const featSub = pickText(L.features?.sub, lang, t("landing.features.sub"));
  const customItems = Array.isArray(L.features?.items) && L.features.items.length > 0 ? L.features.items : null;

  const contacts = Array.isArray(L.contacts) ? L.contacts.filter((c) => c && c.value) : [];
  const footerText = pickText(L.footer, lang, t("landing.footer", { year: new Date().getFullYear(), wa: SUPPORT_WHATSAPP }));

  const social = L.social || {};
  const testimonials = Array.isArray(social.testimonials) ? social.testimonials.filter((x) => x && (pickText(x.quote, lang, "") || x.name)) : [];
  const partners = Array.isArray(social.partners) ? social.partners.filter((x) => x && x.logo_id) : [];
  const socHeading = pickText(social.heading, lang, "موثوق من عملائنا");
  const socSub = pickText(social.sub, lang, "");
  const showSocial = testimonials.length > 0 || partners.length > 0;

  const hasTerms = !!pickText(L.terms?.body, lang, "");
  const hasPrivacy = !!pickText(L.privacy?.body, lang, "");
  const legalData = legal === "terms" ? L.terms : legal === "privacy" ? L.privacy : null;

  const customPlans = Array.isArray(L.plans) && L.plans.length ? L.plans.map((p) => ({
    id: p.id,
    name: pickText(p.name, lang, ""),
    tagline: pickText(p.tagline, lang, ""),
    price: (p.price === "" || p.price == null) ? null : String(p.price),
    original: p.original ? String(p.original) : null,
    popular: !!p.popular,
    features: (p.features || []).map((f) => pickText(f, lang, "")).filter(Boolean),
    missing: (p.missing || []).map((f) => pickText(f, lang, "")).filter(Boolean),
  })) : null;
  const plansToRender = customPlans || PLANS;

  return (
    <div dir={dir} className="min-h-screen bg-slate-50" data-testid="landing-page">
      <PhoneLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      <EmployeeLoginDialog open={empLoginOpen} onOpenChange={setEmpLoginOpen} />
      <LoginChooser open={chooserOpen} onOpenChange={setChooserOpen}
        onCompany={() => { setChooserOpen(false); setLoginOpen(true); }}
        onEmployee={() => { setChooserOpen(false); setEmpLoginOpen(true); }} />

      <Dialog open={!!legal} onOpenChange={(o) => !o && setLegal(null)}>
        <DialogContent className="sm:max-w-2xl font-body max-h-[85vh] overflow-y-auto" dir={dir} data-testid="legal-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
            <DialogTitle className="font-heading text-xl">{pickText(legalData?.title, lang, legal === "terms" ? "الشروط والأحكام" : "سياسة الخصوصية")}</DialogTitle>
            <DialogDescription className="sr-only">{legal === "terms" ? "الشروط والأحكام" : "سياسة الخصوصية"}</DialogDescription>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-slate-600 leading-relaxed" data-testid="legal-body">
            {pickText(legalData?.body, lang, "")}
          </div>
        </DialogContent>
      </Dialog>

      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-3 flex items-center justify-between gap-1.5">
          <Logo size="sm" className="shrink-0" />
          <div className="flex items-center gap-1.5 shrink-0">
            <LanguageSwitcher />
            <Button variant="outline" size="sm" data-testid="landing-install-pwa" onClick={doInstall}
              className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-2">
              <Download className="h-4 w-4 shrink-0" /><span className="text-xs whitespace-nowrap">بوابة الشركات</span>
            </Button>
            <Button variant="ghost" size="sm" data-testid="landing-login-button" onClick={() => setChooserOpen(true)} className="text-slate-600 px-2 text-xs">
              {t("landing.nav.login")}
            </Button>
            <Button size="sm" data-testid="landing-start-nav" onClick={login}
              style={{ backgroundColor: accent }} className="hidden sm:inline-flex text-white hover:brightness-110 gap-1.5">
              {t("landing.nav.startFree")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden text-white" style={heroStyle}>
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20 md:py-28 relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-5">
              <BellRing className="h-3.5 w-3.5" /> {heroBadge}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.15]">
              {heroTitle}
            </h1>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
              {heroSubtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" data-testid="landing-start-trial" onClick={login}
                style={{ backgroundColor: accent }}
                className="text-white hover:brightness-110 font-semibold gap-2 h-12 px-6">
                {heroStart} <ArrowLeft className={`h-4 w-4 ${dir === "ltr" ? "rotate-180" : ""}`} />
              </Button>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="landing-whatsapp-hero"
                className="inline-flex items-center gap-2 rounded-md h-12 px-6 bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold transition">
                <MessageCircle className="h-5 w-5" /> {heroWa}
              </a>
            </div>
          </motion.div>
        </div>
        <ShieldCheck className="absolute -left-16 -bottom-16 h-80 w-80 text-white/5" />
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900">{featHeading}</h2>
          <p className="mt-3 text-slate-500 text-base">{featSub}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {customItems
            ? customItems.map((it, i) => {
                const Icon = ICON_MAP[it.icon] || ICON_MAP.BellRing;
                return <Feature key={i} icon={Icon} i={i} accent={accent}
                  title={pickText(it.title, lang, "")} desc={pickText(it.desc, lang, "")} />;
              })
            : FEATURE_ICONS.map((Icon, i) => (
                <Feature key={i} icon={Icon} accent={accent}
                  title={t(`landing.features.items.${i}.title`)} desc={t(`landing.features.items.${i}.desc`)} i={i} />
              ))}
        </div>
      </section>

      {/* Social proof — testimonials & partners */}
      {showSocial && (
        <section className="bg-white border-t border-slate-100" data-testid="landing-social">
          <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-heading text-3xl font-extrabold text-slate-900">{socHeading}</h2>
              {socSub && <p className="mt-3 text-slate-500 text-base">{socSub}</p>}
            </div>
            {testimonials.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-testimonials">
                {testimonials.map((tm, i) => {
                  const rating = Math.max(0, Math.min(5, Number(tm.rating) || 5));
                  const initials = (tm.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("");
                  return (
                    <motion.div key={i} data-testid={`landing-testimonial-${i}`}
                      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 flex flex-col">
                      <div className="flex gap-0.5 mb-3">
                        {Array.from({ length: 5 }).map((_, s) => (
                          <Star key={s} className={`h-4 w-4 ${s < rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                        ))}
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed flex-1">“{pickText(tm.quote, lang, "")}”</p>
                      <div className="mt-4 flex items-center gap-3">
                        {tm.avatar_id ? (
                          <img src={fileUrl(tm.avatar_id)} alt={tm.name} className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded-full grid place-items-center text-white font-bold text-sm" style={{ backgroundColor: accent }}>{initials}</div>
                        )}
                        <div>
                          <p className="font-heading font-bold text-slate-800 text-sm">{tm.name}</p>
                          {tm.role && <p className="text-xs text-slate-400">{tm.role}</p>}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
            {partners.length > 0 && (
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 items-center" data-testid="landing-partners">
                {partners.map((p, i) => (
                  <div key={i} data-testid={`landing-partner-${i}`} className="flex items-center justify-center p-3">
                    <img src={fileUrl(p.logo_id)} alt={p.name || "partner"} title={p.name || ""}
                      className="max-h-14 w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Pricing */}
      <section className="bg-white border-t border-slate-100" data-testid="landing-pricing">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              {t("landing.pricing.badge")}
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">{t("landing.pricing.heading")}</h2>
            <p className="mt-3 text-slate-500 text-base">{t("landing.pricing.sub")}</p>
            <div className="mt-6 flex flex-col items-center gap-4">
              <OfferCountdown />
              <BillingToggle billing={billing} setBilling={setBilling} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {plansToRender.map((p, i) => <PlanCard key={p.id} plan={p} i={i} billing={billing} />)}
          </div>
          <div className="mt-12 text-center">
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="landing-whatsapp-pricing"
              className="inline-flex items-center justify-center gap-2 rounded-md h-12 px-8 bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold transition">
              <MessageCircle className="h-5 w-5" /> {t("landing.pricing.whatsappCta")} {SUPPORT_WHATSAPP}
            </a>
          </div>
        </div>
      </section>

      <LandingTrust />

      <section className="bg-[#0b131f] py-16 sm:py-20 px-4 sm:px-8 text-white relative overflow-hidden" data-testid="landing-contact-section">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-600/10 blur-3xl" />
        <div className="mx-auto max-w-6xl relative">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold">{L.contactHeading || DEFAULT_CONTACT_HEADING}</h2>
            <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">{L.contactSub || DEFAULT_CONTACT_SUB}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {((L.cards && L.cards.length) ? L.cards : DEFAULT_CARDS).map((c, i) => (
              <div key={i} data-testid={`contact-card-${i}`}
                className="group rounded-2xl bg-[#131d2e] border border-white/10 p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/40 hover:shadow-[0_0_30px_rgba(16,185,129,0.15)]">
                <div className="h-12 w-12 rounded-xl bg-emerald-600/15 flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-emerald-400" />
                </div>
                <p className="text-[11px] font-semibold tracking-wide text-emerald-400/90 mb-1">{c.cat}</p>
                <h3 className="font-heading font-bold text-lg mb-3">{c.title}</h3>
                <a href={`mailto:${c.email}`} dir="ltr" className="text-sm text-slate-200 hover:text-emerald-300 transition text-right block mb-3 font-mono-nums">{c.email}</a>
                <ul className="space-y-1.5 text-xs text-slate-400 mb-5 flex-1">
                  {(c.extra || []).map((e, j) => (
                    <li key={j} className="flex items-center gap-2 justify-end" dir={/\+?\d/.test(e) ? "ltr" : "rtl"}>
                      {/\+?\d/.test(e) ? <Phone className="h-3.5 w-3.5 text-emerald-400/70 order-first" /> : <span className="h-1 w-1 rounded-full bg-emerald-400/60" />}
                      <span className={/\+?\d/.test(e) ? "font-mono-nums" : ""}>{e}</span>
                    </li>
                  ))}
                </ul>
                <a href={`mailto:${c.email}`} data-testid={`contact-card-action-${i}`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2.5 transition-colors">
                  {c.action} <ArrowLeft className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#070d16] text-slate-300 border-t border-slate-800/80" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr_1fr_1fr] gap-10 md:gap-8">
            <div className="space-y-4">
              <Logo size="md" textClass="text-white" />
              <p className="text-sm text-slate-400 leading-relaxed max-w-xs">{L.footerTagline || DEFAULT_FOOTER_TAGLINE}</p>
              <div className="flex items-center gap-2.5 pt-1">
                {[["linkedin", Linkedin], ["twitter", Twitter], ["facebook", Facebook], ["youtube", Youtube]].map(([key, Ic], i) => {
                  const href = (L.socialLinks || DEFAULT_SOCIAL)[key] || "";
                  return (
                    <a key={i} href={href || "#"} target={href ? "_blank" : undefined} rel="noreferrer" data-testid={`footer-social-${i}`}
                      className={`h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors ${href ? "" : "opacity-40 pointer-events-none"}`}>
                      <Ic className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
            {((L.footerCols && L.footerCols.length) ? L.footerCols : DEFAULT_FOOTER_COLS).map((col, i) => (
              <div key={i}>
                <h4 className="font-heading font-bold text-white text-sm mb-4">{col.h}</h4>
                <ul className="space-y-2.5">
                  {(col.links || []).map((l, j) => (
                    <li key={j}><a href={l.slug ? `/page/${l.slug}` : (l.url || "#")} data-testid={`footer-link-${l.slug || j}`} className="text-sm text-slate-400 hover:text-emerald-300 transition-colors">{l.label}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {contacts.length > 0 && (
            <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center gap-3" data-testid="landing-contacts">
              {contacts.map((c, i) => {
                const preset = contactPreset(c.type);
                const Icon = ICON_MAP[preset.icon] || ICON_MAP.Globe;
                return (
                  <a key={i} href={preset.link(c.value)} target="_blank" rel="noreferrer" data-testid={`landing-contact-${c.type}-${i}`}
                    title={pickText(c.label, lang, preset.label)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 hover:border-emerald-500/40 hover:text-white transition">
                    <Icon className="h-4 w-4 text-emerald-400" />
                    <span>{pickText(c.label, lang, preset.label)}</span>
                  </a>
                );
              })}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p>{footerText}</p>
            {(hasTerms || hasPrivacy) && (
              <div className="flex items-center gap-4">
                {hasTerms && <button data-testid="footer-terms-link" onClick={() => setLegal("terms")} className="hover:text-emerald-300 transition underline-offset-4 hover:underline">{pickText(L.terms?.title, lang, "الشروط والأحكام")}</button>}
                {hasTerms && hasPrivacy && <span className="text-slate-700">·</span>}
                {hasPrivacy && <button data-testid="footer-privacy-link" onClick={() => setLegal("privacy")} className="hover:text-emerald-300 transition underline-offset-4 hover:underline">{pickText(L.privacy?.title, lang, "سياسة الخصوصية")}</button>}
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
